package com.example.borshi.web.cart

import kotlin.js.Date

// --- Enums ---

enum class OrderFrequency {
    ONCE,
    DAILY,
    ALTERNATE_DAYS,
    WEEKLY,
    BIWEEKLY,
    CUSTOM_DAYS
}

enum class DeliverySlot {
    MORNING, // 5:00 AM - 7:30 AM
    EVENING  // 5:00 PM - 7:30 PM
}

enum class Weekday {
    MON, TUE, WED, THU, FRI, SAT, SUN
}

enum class SubscriptionPayRule {
    PAY_PER_DELIVERY,
    PAY_FIRST_DELIVERY_TODAY,
    PREPAID_MONTHLY
}

// --- Data Structures ---

data class SubscriptionSchedule(
    val frequency: OrderFrequency,
    val startDate: Long, // Epoch millis
    val endDate: Long? = null,
    val slot: DeliverySlot = DeliverySlot.MORNING,
    val customDays: List<Weekday> = emptyList()
) {
    fun toSummaryString(): String {
        return when (frequency) {
            OrderFrequency.DAILY -> "Daily (${slot.name.lowercase().capitalize()})"
            OrderFrequency.ALTERNATE_DAYS -> "Alternate Days (${slot.name.lowercase().capitalize()})"
            OrderFrequency.WEEKLY -> "Weekly (${slot.name.lowercase().capitalize()})"
            OrderFrequency.BIWEEKLY -> "Bi-weekly (${slot.name.lowercase().capitalize()})"
            OrderFrequency.CUSTOM_DAYS -> "Custom Days (${customDays.joinToString { it.name.take(3) }})"
            else -> "Standard Delivery"
        }
    }
}

// Sealed class for polymorphic cart items
sealed class CartLineItem {
    abstract val id: String
    abstract val productId: String
    abstract val name: String
    abstract val price: Double
    abstract val imageUrl: String?

    // Unique key to identify item in cart (idempotency)
    abstract fun uniqueKey(): String

    data class OneTime(
        override val id: String,
        override val productId: String,
        override val name: String,
        override val price: Double,
        override val imageUrl: String? = null,
        val weight: String,
        val quantity: Int
    ) : CartLineItem() {
        override fun uniqueKey() = "ot_$id"
    }

    data class Subscription(
        override val id: String,
        override val productId: String,
        override val name: String, // e.g. "Cow Milk"
        override val price: Double,
        override val imageUrl: String? = null,
        val packSize: String, // e.g. "500ml"
        val quantityPerDelivery: Int,
        val schedule: SubscriptionSchedule,
        val payRule: SubscriptionPayRule = SubscriptionPayRule.PAY_FIRST_DELIVERY_TODAY
    ) : CartLineItem() {
        override fun uniqueKey() = "sub_$id"
    }
}

data class CartTotals(
    val oneTimeSubtotal: Double = 0.0,
    val subscriptionFirstDeliverySubtotal: Double = 0.0,
    val deliveryFee: Double = 0.0,
    val taxAndPackaging: Double = 0.0,
    val payTodayTotal: Double = 0.0,
    val itemCount: Int = 0
)

data class Address(
    val line1: String,
    val city: String,
    val postalCode: String,
    val coordinates: Pair<Double, Double>? = null
)

data class OrderRequest(
    val itemsOneTime: List<CartLineItem.OneTime>,
    val itemsSubscription: List<CartLineItem.Subscription>,
    val totals: CartTotals,
    val customerName: String,
    val customerPhone: String,
    val address: Address,
    val paymentMethod: String, // "COD" or "ONLINE"
    val timestamp: Long = Date().getTime().toLong()
)

// Extension helper
private fun String.capitalize() = replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
